import { AnyAction } from 'redux';
import { handleActions, combineActions } from 'redux-actions';

import { logoutAction } from '@reducers/auth/AuthActionConfig';
import { getLabOrderMessageListAction, getMessageListAction, getClinicMessageListAction } from '@reducers/dashboard/DashboardActionConfig';
import { getUnreadNotificationsAction } from '@reducers/shared/SharedActionConfig';

import {
  getLaboratoryOrdersAction,
  getLaboratoryLabOrderMessageThreadAction,
  getLabOrderMessageThreadAction,
  sendLabOrderMessageAction,
  updateLaboratoryLabOrderStatusAction,
  getLabOrdersAction,
  getLabOrderMessagesAction,
  getLabOrderMessageAttachmentsAction,
  deleteLabOrderMessageAttachmentAction,
  deleteLabOrderAttachmentAction,
  markLabOrderConversationReadAction,
  markLabOrderConversationInListReadAction,
  markLabOrderConversationInListUnreadAction,

  clearLabOrderDataAction,
  displayLabOrderMessageAction,
  updateLabOrderMessageAction,
  deleteLabOrderMessageAction,
  deleteLabOrderReceiverMessageAction,
  updateUnreadLabOrderMessageCountAction,
  updateLabOrderListAction,
  updateLabOrderMessageStatusAction,
  updateLabOrderFollowUpStatusAction,
  updateLaboratoryFollowUpStatusAction,
} from './LabOrderActionConfig';

type LabOrderStatus = 'pending' | 'read' | 'accepted' | 'ongoing' | 'sent' | 'received' | 'completed' | 'declined' | 'all';

// reducers
const defaultState = {
  labOrderData: {} as any,
  labOrderList: {} as any,
  messageList: {},
  conversations: {} as { [key: string]: any[] },
  pagination: {},
  unreadCount: {
    pending: 0,
    accepted: 0,
    ongoing: 0,
    sent: 0,
    received: 0,
    declined: 0,
    completed: 0,
    all: 0,
  } as any,
};

type State = typeof defaultState;

const reducer = handleActions<State, AnyAction>({
  [`${combineActions(getLabOrderMessageThreadAction, getLaboratoryLabOrderMessageThreadAction)}`](state: State, { payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): both thread endpoints now answer {data: <thread>} instead of it directly.
    const payload = rawPayload?.data?.id ? rawPayload.data : rawPayload;
    return {
      ...state,
      labOrderData: payload,
    };
  },
  [`${getLabOrdersAction}`](state: State, { params, payload }: AnyAction) {
    const newPayload = payload;
    newPayload.tab = params?.status;
    return {
      ...state,
      labOrderList: newPayload,
    };
  },
  [`${getLabOrderMessageListAction}`](state: State, { payload }: AnyAction) {
    return {
      ...state,
      messageList: payload,
    };
  },
  [`${getLabOrderMessagesAction}`](state: State, { payload, params }: AnyAction) {
    const paramId: string = params?.id;
    const conversations: any = paramId && state?.conversations ? state?.conversations[paramId] : [];
    const {
      data,
      meta: { current_page: page, last_page: lastPage },
    } = payload;
    const messages = page === 1 ? data : [ ...conversations, ...data ];
    return {
      ...state,
      conversations: {
        ...state.conversations,
        [params.id]: [
          ...messages,
        ],
      },
      pagination: {
        ...state.pagination,
        [params.id]: {
          current_page: page,
          last_page: lastPage,
        },
      },
    };
  },
  [`${displayLabOrderMessageAction}`](state: State, { payload }: AnyAction) {
    const conversations = state.conversations?.[payload.referral_id] ?? [];
    const newLabOrderData: any = { ...state.labOrderData };

    if (payload?.userId !== payload?.sender_id && payload?.referral_id === newLabOrderData?.id) {
      newLabOrderData.message = payload;

      return {
        ...state,
        labOrderData: newLabOrderData,
        conversations: {
          ...state.conversations,
          [payload.referral_id]: [
            payload,
            ...conversations,
          ],
        },
      };
    }

    if (payload?.url && !payload?.userId) {
      if (newLabOrderData.attachments.length > 0) {
        newLabOrderData.attachments.unshift(payload?.attachment);
      }

      if (newLabOrderData.attachments.length === 0) {
        newLabOrderData.attachments.push(payload?.attachment);
      }

      return {
        ...state,
        labOrderData: newLabOrderData,
        conversations: {
          ...state.conversations,
          [payload.referral_id]: [
            payload,
            ...conversations,
          ],
        },
      };
    }

    return {
      ...state,
      conversations: {
        ...state.conversations,
        [payload.referral_id]: [
          payload,
          ...conversations,
        ],
      },
    };
  },
  [`${updateLabOrderMessageAction}`](state: State, { payload }: AnyAction) {
    const conversations = state.conversations?.[payload.referral_id] ?? [];
    const messageIndex = conversations.findIndex((c: any) => c.id === payload.id && !!c.status);

    return {
      ...state,
      conversations: {
        ...state.conversations,
        [payload.referral_id]: [
          ...conversations.slice(0, messageIndex),
          { ...conversations[messageIndex], ...payload },
          ...conversations.slice(messageIndex + 1),
        ],
      },
    };
  },
  [`${deleteLabOrderMessageAction}`](state: State, { payload }: AnyAction) {
    const conversations = state.conversations?.[payload.referral_id] ?? [];
    const updatedConversation = conversations.filter((c: any) => c.id !== payload.id);
    return {
      ...state,
      conversations: {
        ...state.conversations,
        [payload.referral_id]: updatedConversation,
      },
    };
  },
  [`${sendLabOrderMessageAction}`](state: State, { payload: rawPayload, params }: AnyAction) {
    // Phase 3 (backend): now answers {data: <message>} instead of the message directly.
    const payload = rawPayload?.data?.referral_id ? rawPayload.data : rawPayload;
    const conversations = state.conversations?.[payload.referral_id] ?? [];
    const messageIndex = conversations.findIndex((c: any) => c.id === params.id && c.status === 'sending');

    if (payload?.url) {
      const newLabOrderData: any = { ...state.labOrderData };

      if (newLabOrderData.attachments.length > 0) {
        newLabOrderData.attachments.unshift(payload?.attachment);
      }

      if (newLabOrderData.attachments.length === 0) {
        newLabOrderData.attachments.push(payload?.attachment);
      }

      return {
        ...state,
        labOrderData: newLabOrderData,
        conversations: {
          ...state.conversations,
          [payload.referral_id]: [
            ...conversations.slice(0, messageIndex),
            payload,
            ...conversations.slice(messageIndex + 1),
          ],
        },
      };
    }

    return {
      ...state,
      conversations: {
        ...state.conversations,
        [payload.referral_id]: [
          ...conversations.slice(0, messageIndex),
          payload,
          ...conversations.slice(messageIndex + 1),
        ],
      },
    };
  },
  [`${getLaboratoryOrdersAction}`](state: State, { params, payload }: AnyAction) {
    const newPayload = payload;
    newPayload.tab = params?.status;
    return {
      ...state,
      labOrderList: newPayload,
    };
  },
  [`${updateLaboratoryLabOrderStatusAction}`](state: State, { payload, params }: AnyAction) {
    const labOrder: any = { ...state.labOrderData };
    const userId = params?.userId;
    // Phase 3 (backend): now answers {data: <lab order>} instead of it directly.
    const labOrderData = payload?.data?.id ? payload.data : payload;
    // let newCount: any = { ...state.unreadCount };

    if (labOrder.status === 'pending' && labOrder?.created_by !== userId) {
      // newCount = {
      //   ...newCount,
      //   received: state.unreadCount.received - 1,
      //   all: state.unreadCount.all - 1,
      // };

      return {
        ...state,
        labOrderData,
        // unreadCount: newCount,
      };
    }

    return {
      ...state,
      labOrderData,
      // unreadCount: newCount,
    };
  },
  [`${getLabOrderMessageAttachmentsAction}`](state: State, { payload, params }: AnyAction) {
    if (params?.for_download_all) {
      return state;
    }
    const newData = payload?.data;
    const newLabOrderData: any = { ...state.labOrderData };
    newLabOrderData.attachments.push(...newData);
    return {
      ...state,
      labOrderData: newLabOrderData,
    };
  },
  [`${deleteLabOrderMessageAttachmentAction}`](state: State, { params }: AnyAction) {
    const newConversations: any = state.conversations?.[params?.data?.id];
    const updatedConversation = newConversations.filter(
      (c: any) => c.id !== params?.data?.conversation_id);
    const newLabOrderData: any = { ...state.labOrderData };
    const updatedAttachments = newLabOrderData.attachments
      .filter((a: any) => a.id !== params?.data?.attachment_id);
    newLabOrderData.attachments = updatedAttachments;
    return {
      ...state,
      labOrderData: newLabOrderData,
      conversations: {
        ...state.conversations,
        [params?.data?.id]: updatedConversation,
      },
    };
  },
  [`${deleteLabOrderAttachmentAction}`](state: State, { payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): now answers {data: <conversation or attachment>} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    if (payload?.attachment_id) {
      const newConversations: any = state.conversations?.[payload?.referral_id];
      const updatedConversation = newConversations.filter((c: any) => c.id !== payload?.id);
      const newLabOrderData: any = { ...state.labOrderData };
      const updatedAttachments = newLabOrderData.attachments
        .filter((a: any) => a.id !== payload?.attachment_id);
      newLabOrderData.attachments = updatedAttachments;

      return {
        ...state,
        labOrderData: newLabOrderData,
        conversations: {
          ...state.conversations,
          [payload?.referral_id]: updatedConversation,
        },
      };
    }
    const newLabOrderData: any = { ...state.labOrderData };
    const updatedAttachments = newLabOrderData.attachments
      .filter((a: any) => a.id !== payload?.id);
    newLabOrderData.attachments = updatedAttachments;
    return {
      ...state,
      labOrderData: newLabOrderData,
    };
  },
  [`${deleteLabOrderReceiverMessageAction}`](state: State, { payload }: AnyAction) {
    const newConversations: any = state.conversations?.[payload?.referral_id];
    const updatedConversation = newConversations.filter((c: any) => c.id !== payload?.id);
    const newLabOrderData: any = { ...state.labOrderData };
    const updatedAttachments = newLabOrderData.attachments
      .filter((a: any) => a.id !== payload?.attachment_id);
    newLabOrderData.attachments = updatedAttachments;
    return {
      ...state,
      labOrderData: newLabOrderData,
      conversations: {
        ...state.conversations,
        [payload?.referral_id]: updatedConversation,
      },
    };
  },
  [`${getUnreadNotificationsAction}`](state: State, { payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): unread-notifications now answers {data: {...}} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    return {
      ...state,
      unreadCount: payload?.lab_orders,
    };
  },
  [`${combineActions(getMessageListAction, getClinicMessageListAction)}`](state: State, { payload }: AnyAction) {
    return {
      ...state,
      messageList: payload,
    };
  },
  [`${markLabOrderConversationReadAction}`](state: State, { params, payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): now answers {data: <read record>} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    const labOrder: any = { ...state.labOrderData };
    const userId = params?.userId;
    const patient: any = labOrder?.patient;
    const message: any = labOrder?.message;

    // if last message already have a read record, return;
    if (message?.reads?.id) return state;

    let newCount: any = state.unreadCount;

    newCount = {
      ...newCount,
      [labOrder.status as LabOrderStatus]: state
        .unreadCount[labOrder.status as LabOrderStatus] - 1,
      all: state.unreadCount.all - 1,
    };

    if ([ 'pending', 'read', 'accepted', 'ongoing', 'declined' ].indexOf(labOrder?.status) !== -1
      && patient?.created_by === userId) {
      newCount = {
        ...newCount,
        sent: state.unreadCount.sent - 1,
        all: state.unreadCount.all - 1,
      };
    }

    if ([ 'pending', 'read', 'accepted', 'ongoing', 'declined' ].indexOf(labOrder?.status) !== -1
      && patient?.created_by !== userId) {
      newCount = {
        ...newCount,
        received: state.unreadCount.received - 1,
        all: state.unreadCount.all - 1,
      };
    }

    const newMessage = { ...message, reads: payload };
    const newLabOrderData = { ...labOrder, message: newMessage };
    return {
      ...state,
      labOrderData: newLabOrderData,
      unreadCount: newCount,
    };
  },
  [`${markLabOrderConversationInListReadAction}`](state: State, { params, payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): now answers {data: <read record>} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    const { data } = params;
    const type = data?.type ?? 'lab-order-list';
    let newCount: any = { ...state.unreadCount };
    if (type === 'message-list') {
      const labOrderMessages: any = { ...state.messageList };
      const messageIndex = labOrderMessages?.data?.length > 0 && labOrderMessages.data
        .findIndex((r: any) => r.id === data.lab_order_id);

      if (messageIndex !== -1) {
        const labOrder = { ...labOrderMessages.data[messageIndex] };

        if (labOrder?.message) {
          if (!labOrder?.message?.reads?.id) {
            newCount = {
              ...newCount,
              [labOrder.status as LabOrderStatus]: state
                .unreadCount[labOrder.status as LabOrderStatus] - 1,
              all: state.unreadCount.all - 1,
            };

            if ([ 'pending', 'read', 'accepted', 'ongoing', 'declined' ].indexOf(labOrder?.status) !== -1
            && labOrder?.patient?.created_by === data?.user_id) {
              newCount = {
                ...newCount,
                sent: state.unreadCount.sent - 1,
                all: state.unreadCount.all - 1,
              };
            }

            if ([ 'pending', 'read', 'accepted', 'ongoing', 'declined' ].indexOf(labOrder?.status) !== -1
            && labOrder?.patient?.created_by !== data?.user_id) {
              newCount = {
                ...newCount,
                received: state.unreadCount.received - 1,
                all: state.unreadCount.all - 1,
              };
            }

            labOrder.message = {
              ...labOrder.message,
              reads: payload,
            };
          }
        }

        labOrderMessages.data = [
          ...labOrderMessages.data.slice(0, messageIndex),
          labOrder,
          ...labOrderMessages.data.slice(messageIndex + 1),
        ];
      }

      return {
        ...state,
        messageList: labOrderMessages,
        unreadCount: newCount,
      };
    }
    const labOrders: any = { ...state.labOrderList };
    const labOrderIndex = labOrders?.data?.length > 0 && labOrders.data
      .findIndex((r: any) => r.id === data.lab_order_id);

    if (labOrderIndex !== -1) {
      const labOrder = { ...labOrders.data[labOrderIndex] };

      if (labOrder?.message) {
        if (!labOrder?.message?.reads?.id) {
          newCount = {
            ...newCount,
            [labOrder.status as LabOrderStatus]: state
              .unreadCount[labOrder.status as LabOrderStatus] - 1,
            all: state.unreadCount.all - 1,
          };

          if ([ 'pending', 'read', 'accepted', 'ongoing', 'declined' ].indexOf(labOrder?.status) !== -1
            && labOrder?.patient?.created_by === data?.user_id) {
            newCount = {
              ...newCount,
              sent: state.unreadCount.sent - 1,
              all: state.unreadCount.all - 1,
            };
          }

          if ([ 'pending', 'read', 'accepted', 'ongoing', 'declined' ].indexOf(labOrder?.status) !== -1
            && labOrder?.patient?.created_by !== data?.user_id) {
            newCount = {
              ...newCount,
              received: state.unreadCount.received - 1,
              all: state.unreadCount.all - 1,
            };
          }

          labOrder.message = {
            ...labOrder.message,
            reads: payload,
          };
        }
      }

      labOrders.data = [
        ...labOrders.data.slice(0, labOrderIndex),
        labOrder,
        ...labOrders.data.slice(labOrderIndex + 1),
      ];
    }

    return {
      ...state,
      labOrderList: labOrders,
      unreadCount: newCount,
    };
  },
  [`${markLabOrderConversationInListUnreadAction}`](state: State, { params }: AnyAction) {
    const { data } = params;
    const type = data?.type ?? 'lab-order-list';
    let newCount: any = { ...state.unreadCount };
    if (type === 'message-list') {
      const labOrderMessages: any = { ...state.messageList };
      const messageIndex = labOrderMessages?.data?.length > 0 && labOrderMessages.data
        .findIndex((r: any) => r.id === data.lab_order_id);

      if (messageIndex !== -1) {
        const labOrder = { ...labOrderMessages.data[messageIndex] };

        if (labOrder?.message) {
          if (labOrder?.message?.reads?.id) {
            newCount = {
              ...newCount,
              [labOrder.status as LabOrderStatus]: state
                .unreadCount[labOrder.status as LabOrderStatus] + 1,
              all: state.unreadCount.all + 1,
            };

            if ([ 'pending', 'read', 'accepted', 'ongoing', 'declined' ].indexOf(labOrder?.status) !== -1
            && labOrder?.patient?.created_by === data?.user_id) {
              newCount = {
                ...newCount,
                sent: state.unreadCount.sent + 1,
                all: state.unreadCount.all + 1,
              };
            }

            if ([ 'pending', 'read', 'accepted', 'ongoing', 'declined' ].indexOf(labOrder?.status) !== -1
            && labOrder?.patient?.created_by !== data?.user_id) {
              newCount = {
                ...newCount,
                received: state.unreadCount.received + 1,
                all: state.unreadCount.all + 1,
              };
            }

            labOrder.message = {
              ...labOrder.message,
              reads: null,
            };
          }
        }

        labOrderMessages.data = [
          ...labOrderMessages.data.slice(0, messageIndex),
          labOrder,
          ...labOrderMessages.data.slice(messageIndex + 1),
        ];
      }

      return {
        ...state,
        messageList: labOrderMessages,
        unreadCount: newCount,
      };
    }
    const labOrders: any = { ...state.labOrderList };
    const labOrderIndex = labOrders?.data?.length > 0 && labOrders.data
      .findIndex((r: any) => r.id === data.lab_order_id);

    if (labOrderIndex !== -1) {
      const labOrder = { ...labOrders.data[labOrderIndex] };

      if (labOrder?.message) {
        if (labOrder?.message?.reads?.id) {
          newCount = {
            ...newCount,
            [labOrder.status as LabOrderStatus]: state
              .unreadCount[labOrder.status as LabOrderStatus] + 1,
            all: state.unreadCount.all + 1,
          };

          if ([ 'pending', 'read', 'accepted', 'ongoing', 'declined' ].indexOf(labOrder?.status) !== -1
            && labOrder?.patient?.created_by === data?.user_id) {
            newCount = {
              ...newCount,
              sent: state.unreadCount.sent + 1,
              all: state.unreadCount.all + 1,
            };
          }

          if ([ 'pending', 'read', 'accepted', 'ongoing', 'declined' ].indexOf(labOrder?.status) !== -1
            && labOrder?.patient?.created_by !== data?.user_id) {
            newCount = {
              ...newCount,
              received: state.unreadCount.received + 1,
              all: state.unreadCount.all + 1,
            };
          }

          labOrder.message = {
            ...labOrder.message,
            reads: null,
          };
        }
      }

      labOrders.data = [
        ...labOrders.data.slice(0, labOrderIndex),
        labOrder,
        ...labOrders.data.slice(labOrderIndex + 1),
      ];
    }

    return {
      ...state,
      labOrderList: labOrders,
      unreadCount: newCount,
    };
  },
  [`${updateUnreadLabOrderMessageCountAction}`](state: State, { payload }: AnyAction) {
    const userId = payload?.userId;
    const payloadData = payload?.data;
    const patient = payload?.data?.referred_details?.patient;
    const lastMessagesSent = payloadData?.referred_details?.last_message_sent || [];
    const lastMessageCount = payloadData?.referred_details?.last_message_sent?.length || 0;

    let newCount: any = state.unreadCount;
    let newLabOrderList: any = state.labOrderList;
    let newMessageList: any = state.messageList;
    const newLabOrderData: any = state.labOrderData;
    // const tab = newReferralList?.tab;

    const { data }: any = newLabOrderList;
    const messageData: any = newMessageList?.data ?? [];

    const labOrderIndex = data?.length > 0 && data
      .findIndex((r: any) => r.id === payloadData.referral_id);
    const messageIndex = messageData?.length > 0 && messageData
      .findIndex((r: any) => r.id === payloadData.referral_id);

    // if the user is in another tab/page, just update the count
    if (labOrderIndex === -1 && messageIndex === -1) {
      // if there is only one unread message, update here
      if (lastMessageCount === 1
        || (lastMessageCount === 2 && lastMessagesSent[1]?.reads?.id)) {
        if (payloadData?.referred_details?.status === 'ongoing') {
          newCount = {
            ...newCount,
            ongoing: state.unreadCount.ongoing + 1,
            all: state.unreadCount.all + 1,
          };
        }

        if (payloadData?.referred_details?.status === 'completed') {
          newCount = {
            ...newCount,
            completed: state.unreadCount.completed + 1,
            all: state.unreadCount.all + 1,
          };
        }

        if ([ 'pending', 'read', 'accepted', 'ongoing', 'declined' ].indexOf(payloadData?.referred_details?.status) !== -1
          && patient?.created_by === userId) {
          newCount = {
            ...newCount,
            sent: state.unreadCount.sent + 1,
            all: state.unreadCount.all + 1,
          };
        }

        if ([ 'pending', 'read', 'accepted', 'ongoing', 'declined' ].indexOf(payloadData?.referred_details?.status) !== -1
          && patient?.created_by !== userId) {
          newCount = {
            ...newCount,
            received: state.unreadCount.received + 1,
            all: state.unreadCount.all + 1,
          };
        }
      }

      if (lastMessageCount === 2 && !lastMessagesSent[1]?.reads?.id) {
        const lastMessageSent = payloadData?.referred_details?.last_message_sent;
        const statusHistories = payloadData?.referred_details?.status_histories;
        // always index 1 since it will only have an array of 2 data
        const previousMessage = lastMessageSent[1];
        const previousStatus = statusHistories[1];
        // only enter here if the status is being changed to completed
        // and if the previous message is unread
        if (payloadData?.referred_details?.status === 'completed') {
          // if the previous message's read_at is null proceed here via previous status
          // then deduct count
          if (previousStatus?.status === 'ongoing' && !previousMessage?.reads?.id) {
            newCount = {
              ...newCount,
              ongoing: state.unreadCount.ongoing - 1,
              all: state.unreadCount.all - 1,
            };
          }

          if ([ 'pending', 'read', 'accepted', 'ongoing', 'declined' ].indexOf(previousStatus?.status) !== -1
            && patient?.created_by === userId && !previousMessage?.reads?.id) {
            newCount = {
              ...newCount,
              sent: state.unreadCount.sent - 1,
              all: state.unreadCount.all - 1,
            };
          }

          if ([ 'pending', 'read', 'accepted', 'ongoing', 'declined' ].indexOf(previousStatus?.status) !== -1
            && patient?.created_by !== userId && !previousMessage?.reads?.id) {
            newCount = {
              ...newCount,
              received: state.unreadCount.received - 1,
              all: state.unreadCount.all - 1,
            };
          }

          newCount = {
            ...newCount,
            completed: state.unreadCount.completed + 1,
            all: state.unreadCount.all + 1,
          };
        }
        // only enter here if the status is being revert back to read from ongoing
        // or vice versa or to declined and if the previous message is unread
        if ([ 'read', 'declined' ].indexOf(payloadData?.referred_details?.status) !== -1) {
          // if the previous message's read_at is null proceed here via previous status
          // then deduct count
          if (previousStatus?.status === 'ongoing' && !previousMessage?.reads?.id) {
            newCount = {
              ...newCount,
              ongoing: state.unreadCount.ongoing - 1,
              all: state.unreadCount.all - 1,
            };
          }
        }

        if (payloadData?.referred_details?.status === 'ongoing') {
          // if the previous message's read_at is null proceed here via previous status
          // then deduct count
          if (previousStatus?.status === 'read' && !previousMessage?.reads?.id) {
            newCount = {
              ...newCount,
              ongoing: state.unreadCount.ongoing + 1,
              all: state.unreadCount.all + 1,
            };
          }
        }
      }

      return {
        ...state,
        unreadCount: newCount,
      };
    }

    // else update the lab order data message record as well
    // will only update the count if the user is outside the message page
    // but currently in the lab-order/message list page
    if (!newLabOrderData?.id) {
      if (lastMessageCount === 1
        || (lastMessageCount === 2 && lastMessagesSent[1]?.reads?.read_by === userId)) {
        if (payloadData?.referred_details?.status === 'ongoing') {
          newCount = {
            ...newCount,
            ongoing: state.unreadCount.ongoing + 1,
            all: state.unreadCount.all + 1,
          };
        }

        if ([ 'pending', 'read', 'accepted', 'ongoing', 'declined' ].indexOf(payloadData?.referred_details?.status) !== -1
          && patient?.created_by === userId) {
          newCount = {
            ...newCount,
            sent: state.unreadCount.sent + 1,
            all: state.unreadCount.all + 1,
          };
        }

        if ([ 'pending', 'read', 'accepted', 'ongoing', 'declined' ].indexOf(payloadData?.referred_details?.status) !== -1
          && patient?.created_by !== userId) {
          newCount = {
            ...newCount,
            received: state.unreadCount.received + 1,
            all: state.unreadCount.all + 1,
          };
        }
      }

      if (lastMessageCount === 2) {
        const lastMessageSent = payloadData?.referred_details?.last_message_sent;
        const statusHistories = payloadData?.referred_details?.status_histories;
        // always index 1 since it will only have an array of 2 data
        const previousMessage = lastMessageSent[1];
        const previousStatus = statusHistories[1];
        // only enter here if the status is being changed to completed
        // and if the previous message is unread
        if (payloadData?.referred_details?.status === 'completed') {
          // if the previous message's read_at is null proceed here via previous status
          // then deduct count
          if (previousStatus?.status === 'ongoing' && !previousMessage?.reads?.id) {
            newCount = {
              ...newCount,
              ongoing: state.unreadCount.ongoing - 1,
              all: state.unreadCount.all - 1,
            };
          }

          if ([ 'pending', 'read', 'accepted', 'ongoing', 'declined' ].indexOf(previousStatus?.status) !== -1
            && patient?.created_by === userId && !previousMessage?.reads?.id) {
            newCount = {
              ...newCount,
              sent: state.unreadCount.sent - 1,
              all: state.unreadCount.all - 1,
            };
          }

          if ([ 'pending', 'read', 'accepted', 'ongoing', 'declined' ].indexOf(previousStatus?.status) !== -1
            && patient?.created_by !== userId && !previousMessage?.reads?.id) {
            newCount = {
              ...newCount,
              received: state.unreadCount.received - 1,
              all: state.unreadCount.all - 1,
            };
          }

          newCount = {
            ...newCount,
            completed: state.unreadCount.completed + 1,
            all: state.unreadCount.all + 1,
          };
        }
        // only enter here if the status is being revert back to read from ongoing
        // or vice versa or to declined and if the previous message is unread
        if ([ 'read', 'declined' ].indexOf(payloadData?.referred_details?.status) !== -1) {
          // if the previous message's read_at is null proceed here via previous status
          // then deduct count
          if (previousStatus?.status === 'ongoing' && !previousMessage?.reads?.id) {
            newCount = {
              ...newCount,
              ongoing: state.unreadCount.ongoing - 1,
              all: state.unreadCount.all - 1,
            };
          }
        }
        if (payloadData?.referred_details?.status === 'ongoing') {
          // if the previous message's read_at is null proceed here via previous status
          // then deduct count
          if (previousStatus?.status === 'read' && !previousMessage?.reads?.id) {
            newCount = {
              ...newCount,
              ongoing: state.unreadCount.ongoing + 1,
              all: state.unreadCount.all + 1,
            };
          }
        }
      }
    }

    // will enter this validation if the current page/tab
    // opened is the message list/if the message list has a value
    if (messageIndex >= 0 && messageData?.length > 0) {
      const [ updatedMessage ] = messageData.splice(messageIndex, 1);

      // Update the message with the new data
      updatedMessage.message = payloadData;

      // Add the updated message to the beginning of the array
      messageData.unshift(updatedMessage);

      newMessageList = {
        ...newMessageList,
        messageData,
      };
    }

    // will enter this validation if the current page/tab
    // opened is the referral list/if the referral list has a value
    if (labOrderIndex >= 0 && data?.length > 0) {
      const [ updatedData ] = data.splice(labOrderIndex, 1);

      // Update the message with the new data
      updatedData.message = payloadData;

      // Add the updated data to the beginning of the array
      data.unshift(updatedData);

      newLabOrderList = {
        ...newLabOrderList,
        data,
      };
    }

    return {
      ...state,
      unreadCount: newCount,
      labOrderList: newLabOrderList,
      messageList: newMessageList,
    };
  },
  [`${updateLabOrderListAction}`](state: State, { payload }: AnyAction) {
    const { data: newData } = payload.data;
    const newLabOrderList: any = { ...state.labOrderList };
    const newMessageList: any = { ...state.messageList };
    const tab = newLabOrderList?.tab;

    // if status is not pending, update the data
    if (newData?.status !== 'pending') {
      const { data: refData }: any = newLabOrderList;
      const messageData: any = newMessageList?.data ?? [];

      const referralIndex = refData.findIndex((r: any) => r.id === newData.id);
      const messageIndex = messageData.length > 0
        && messageData.findIndex((r: any) => r.id === newData.id);

      // update data list if the user is in the ongoing tab
      if (tab === 'ongoing') {
        // it means the data is not yet visible in the list, add it
        if (newData?.status === 'ongoing' && referralIndex === -1) {
          if (newLabOrderList?.data?.length >= newLabOrderList?.meta?.per_page) {
            // remove the last data
            newLabOrderList?.data?.splice(9, 1);
            // push the newData above the list
            newLabOrderList?.data?.unshift(newData);
          }
          if (newLabOrderList?.data?.length < newLabOrderList?.meta?.per_page) {
            // push the newData above the list
            newLabOrderList?.data?.unshift(newData);
          }
          return {
            ...state,
            labOrderList: newLabOrderList,
          };
        }
        // it means the data is visible and needs to be removed from the list
        const filteredData = refData.filter((r: any) => r.id !== newData.id);
        newLabOrderList.data = filteredData;
        return {
          ...state,
          labOrderList: newLabOrderList,
        };
      }

      // update data list if the user is in the completed tab
      if (tab === 'completed') {
        // it means the data is not yet visible in the list, add it
        if (newData?.status === 'completed' && referralIndex === -1) {
          if (newLabOrderList?.data?.length >= newLabOrderList?.meta?.per_page) {
            // remove the last data
            newLabOrderList?.data?.splice(9, 1);
            // push the newData above the list
            newLabOrderList?.data?.unshift(newData);
          }
          if (newLabOrderList?.data?.length < newLabOrderList?.meta?.per_page) {
            // push the newData above the list
            newLabOrderList?.data?.unshift(newData);
          }
          return {
            ...state,
            labOrderList: newLabOrderList,
          };
        }
        // it means the data is visible and needs to be removed from the list
        const filteredData = refData.filter((r: any) => r.id !== newData.id);
        newLabOrderList.data = filteredData;
        return {
          ...state,
          labOrderList: newLabOrderList,
        };
      }

      // remove completed status data real time in other tabs
      if ([ 'sent', 'received', 'ongoing' ].indexOf(tab) !== -1) {
        if (newData?.status === 'completed' && referralIndex !== -1) {
          // it means the data is visible and needs to be removed from the list
          const filteredData = refData.filter((r: any) => r.id !== newData.id);
          newLabOrderList.data = filteredData;
          return {
            ...state,
            labOrderList: newLabOrderList,
          };
        }
      }

      // if in the referrals tab, update the list status if the index is existing
      if (referralIndex !== -1) {
        refData[referralIndex].status = newData?.status;
        return {
          ...state,
          labOrderList: newLabOrderList,
        };
      }

      // if in the messages table, update the list status if the index is existing
      if (messageIndex !== -1 && messageData?.[messageIndex]) {
        messageData[messageIndex].status = newData?.status;
        return {
          ...state,
          messageList: newMessageList,
        };
      }

      return {
        ...state,
        messageList: state.messageList,
        labOrderList: state.labOrderList,
      };
    }

    // if the status is pending, add the newly created in the list
    // let newCount: any = { ...state.unreadCount };
    // update data list if the user is in the received tab
    if (tab === 'received') {
      if (newLabOrderList?.data?.length >= newLabOrderList?.meta?.per_page) {
        // remove the last data
        newLabOrderList?.data?.splice(9, 1);
        // push the newData above the list
        newLabOrderList?.data?.unshift(newData);
      }
      if (newLabOrderList?.data?.length < newLabOrderList?.meta?.per_page) {
        // push the newData above the list
        newLabOrderList?.data?.unshift(newData);
      }
    }

    // update received count
    // newCount = {
    //   ...newCount,
    //   received: newCount.received + 1,
    //   all: newCount.all + 1,
    // };

    // update message list
    if (newMessageList?.data?.length >= newMessageList?.meta?.per_page) {
      // remove the last data
      newMessageList?.data?.splice(9, 1);
      // push the newData above the list
      newMessageList?.data?.unshift(newData);
    }

    if (newMessageList?.data?.length < newMessageList?.meta?.per_page) {
      // push the newData above the list
      newMessageList?.data?.unshift(newData);
    }

    return {
      ...state,
      // unreadCount: newCount,
      labOrderList: newLabOrderList,
      messageList: newMessageList,
    };
  },
  [`${combineActions(updateLabOrderFollowUpStatusAction, updateLaboratoryFollowUpStatusAction)}`](state: State, { payload }: AnyAction) {
    const updated = payload?.data;

    const updatedLabOrderData = state.labOrderData?.id === updated?.id
      ? { ...state.labOrderData, ...updated }
      : state.labOrderData;

    const updatedLabOrderList = {
      ...state.labOrderList,
      data: state.labOrderList?.data?.map(
        (item: any) => (item.id === updated?.id ? { ...item, ...updated } : item),
      ),
    };

    return {
      ...state,
      labOrderData: updatedLabOrderData,
      labOrderList: updatedLabOrderList,
    };
  },
  [`${updateLabOrderMessageStatusAction}`](state: State, { payload }: AnyAction) {
    const { data: newData } = payload.data;
    const newLabOrderData: any = { ...state.labOrderData };

    // if inside the referral's message, update the data
    if (newLabOrderData?.id === newData?.id) {
      newLabOrderData.status = newData?.status;
      newLabOrderData.status_history = newData?.status_history;
      if (newData?.status === 'completed') {
        newLabOrderData.summary_file_url = newData?.summary_file_url;
        newLabOrderData.summary_file_size = newData?.summary_file_size;
        newLabOrderData.summary_filename = newData?.summary_filename;
      }
      return {
        ...state,
        labOrderData: newLabOrderData,
      };
    }

    return {
      ...state,
      labOrderData: state.labOrderData,
    };
  },
  [`${clearLabOrderDataAction}`](state: State) {
    return {
      ...state,
      labOrderData: {},
    };
  },
  [`${logoutAction}`](state) {
    return {
      ...state,
      unreadCount: defaultState.unreadCount,
    };
  },
}, defaultState);

export default reducer;
